import DownloadIcon from "@mui/icons-material/Download";
import VerifiedIcon from "@mui/icons-material/Verified";
import type { AlertColor } from "@mui/material";
import { Alert, Box, Button, CircularProgress, Container, Snackbar, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { ObserverEmailModal } from "./ObserverEmailModal";
import {
  downloadObserverEnvelopeZip,
  signatureObserverLink,
} from "../../../api/signer";
import DocumentSection from "../../validate-envelope/components/document-section";
import SignersSection from "../../validate-envelope/components/signers-section";

function ObserverComponent(): JSX.Element {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const queryEmail = searchParams.get("email");

  const [email, setEmail] = useState<string>(queryEmail || "");
  const [envelopeData, setEnvelopeData] = useState<ResponseSignatures | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [downloadingZip, setDownloadingZip] = useState<boolean>(false);
  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(!queryEmail);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("info");

  const fetchObserverEnvelope = useCallback(
    async (emailToFetch: string): Promise<void> => {
      if (!code || !emailToFetch) return;
      setLoading(true);
      setErrorMessage("");
      try {
        const response = await signatureObserverLink(code, emailToFetch);
        const data = response.data;
        setEnvelopeData(data);
        setEmail(emailToFetch);
        setEmailModalOpen(false);
      } catch (err: unknown) {
        const errorRes = (err as { response?: { status?: number; data?: { message?: string } } }).response;
        const status = errorRes?.status;
        if (status === 403 || status === 400 || status === 404) {
          setErrorMessage(t("observer.emailModal.accessDenied"));
        } else {
          setErrorMessage(
            errorRes?.data?.message || t("observer.emailModal.accessDenied"),
          );
        }
        setEmailModalOpen(true);
      } finally {
        setLoading(false);
      }
    },
    [code, t],
  );

  useEffect(() => {
    if (queryEmail && code) {
      void fetchObserverEnvelope(queryEmail);
    }
  }, [queryEmail, code, fetchObserverEnvelope]);

  const handleEmailSubmit = (submittedEmail: string): void => {
    void fetchObserverEnvelope(submittedEmail);
  };

  const handleDownloadZip = async (): Promise<void> => {
    if (!code || !email) return;
    setDownloadingZip(true);
    try {
      const response = await downloadObserverEnvelopeZip(code, email);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement("a");
      link.href = blobUrl;
      const downloadName = envelopeData?.id ? envelopeData.id : code;
      link.setAttribute(
        "download",
        `documentos-lote-${downloadName}.zip`,
      );
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("validate.downloadSuccess"));
      setSnackbarMsg(t("validate.downloadSuccess"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch {
      toast.error(t("validate.downloadError"));
      setSnackbarMsg(t("validate.downloadError"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleSnackbarClose = (): void => {
    setSnackbarOpen(false);
  };

  let mainContent: JSX.Element | null = null;

  if (envelopeData?.id) {
    const documentFiles = (envelopeData.files || []) as unknown as Parameters<typeof DocumentSection>[0]["files"];
    const documentSigners = envelopeData.signers as unknown as Parameters<typeof SignersSection>[0]["signers"];

    mainContent = (
      <Stack spacing={3} sx={{ width: "100%" }}>
        <div>
          <Container>
            <Stack
              alignItems={{ xs: "center", md: "center" }}
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  textAlign: { xs: "center", md: "left" },
                }}
                variant="h5"
              >
                {t("observer.info.observerRole")}
              </Typography>
              <Stack
                alignItems="center"
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <Button
                  disabled={downloadingZip}
                  onClick={() => { void handleDownloadZip(); }}
                  startIcon={<DownloadIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  variant="outlined"
                >
                  {t("validate.downloadZip")}
                </Button>
                <VerifiedIcon color="success" sx={{ fontSize: 36 }} />
              </Stack>
            </Stack>
          </Container>
        </div>

        <Box sx={{ backgroundColor: "#fafafa" }}>
          <Container sx={{ paddingY: 3 }}>
            <DocumentSection
              fields={[
                { label: t("validate.fields.author"), value: envelopeData.authorName },
                {
                  label: t("validate.fields.status"),
                  value:
                    envelopeData.status === "SIGNED"
                      ? t("validate.fields.statusSigned")
                      : t("validate.fields.statusPending"),
                },
                {
                  label: t("validate.fields.publishedAt"),
                  value: dayjs(envelopeData.createdAt).format("DD/MM/YYYY HH:mm:ss"),
                },
                { label: t("validate.fields.hash"), value: envelopeData.hash || envelopeData.id },
              ]}
              files={documentFiles}
            />
          </Container>
        </Box>

        <Box>
          <Container sx={{ pb: 4 }}>
            <SignersSection signers={documentSigners} />
          </Container>
        </Box>
      </Stack>
    );
  } else if (loading) {
    mainContent = (
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2, width: "100%" }}>
      {mainContent}

      <ObserverEmailModal
        errorMessage={errorMessage}
        loading={loading}
        onSubmit={handleEmailSubmit}
        open={emailModalOpen}
      />

      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        open={snackbarOpen}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ObserverComponent;
